import { useFormContext } from 'react-hook-form';
import { Box, Button, CircularProgress, Grid, InputAdornment, OutlinedInput, Typography } from '@mui/material';
import InputMask from 'react-input-mask';
import { useTranslation } from 'react-i18next';

import CustomInput from 'app/components/CustomInput';
import CustomSelect from 'app/components/CustomSelect';
import { FormFieldValues } from 'app/pages/anipay/model/types';
import { aniPayTypes } from 'app/pages/anipay/constants';

interface Props {
  handleSearch: VoidFunction;
  isLoading: boolean;
}

const GeneralDetails = (props: Props) => {
  const { t } = useTranslation();
  const { handleSearch, isLoading } = props;

  const {
    register,
    control,
    watch,
    formState: { errors },
    setValue,
  } = useFormContext<FormFieldValues>();

  const watchShowBenificiaryInfo = watch('bank_name');
  const selectedAliasType = watch('aliasType');
  const aliasValue = watch('aliasValue');

  return (
    <Grid container spacing={3.5} sx={{ maxWidth: '670px' }}>
      <Grid item xs={12} md={6}>
        <CustomSelect
          title={t('anipay.ipsAliasType')}
          control={control}
          options={aniPayTypes}
          rest={{ ...register('aliasType') }}
          isValid={!!errors.aliasType?.message}
          errorText={errors.aliasType?.message}
          placeholder={t('anipay.ipsAliasType')}
          onChange={() => {
            setValue('aliasValue', null, {
              shouldValidate: true,
            });
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        {selectedAliasType?.value === 'MOBILE' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography sx={{ fontSize: '15px', fontWeight: 400, lineHeight: 1 }}>
              {t('anipay.ipsAliasValue')}
            </Typography>
            <InputMask
              mask="(99) 999 99 99"
              maskChar={null}
              disabled={!selectedAliasType}
              value={aliasValue || ''}
              onChange={(e) => {
                const { value } = e.target;
                setValue('aliasValue', value, {
                  shouldValidate: true,
                });
              }}
            >
              <OutlinedInput
                placeholder="(00) 000 00 00"
                error={!!errors.aliasValue?.message}
                endAdornment={
                  <Button onClick={handleSearch} size={'small'} color={'secondary'} disabled={!!errors?.aliasValue}>
                    {isLoading ? (
                      <CircularProgress sx={{ width: '12px !important', height: '12px !important' }} />
                    ) : (
                      t('anipay.search')
                    )}
                  </Button>
                }
                startAdornment={
                  <InputAdornment position="start">
                    <Typography sx={{ fontFamily: 'Yelo', fontWeight: 500 }}>+994</Typography>
                  </InputAdornment>
                }
                sx={{
                  width: '100%',
                  '& .MuiInputBase-input': {
                    p: 0,
                  },
                }}
              />
            </InputMask>
            {errors.aliasValue?.message && (
              <Typography sx={{ color: 'red', fontSize: '14px' }}>{errors.aliasValue?.message}</Typography>
            )}
          </Box>
        ) : (
          <CustomInput
            title={t('anipay.ipsAliasValue')}
            placeholder={t('anipay.ipsAliasValue')}
            adornment={
              <Button onClick={handleSearch} size={'small'} color={'secondary'} disabled={!!errors?.aliasValue}>
                {isLoading ? (
                  <CircularProgress sx={{ width: '12px !important', height: '12px !important' }} />
                ) : (
                  t('anipay.search')
                )}
              </Button>
            }
            disabled={!selectedAliasType}
            rest={{
              ...register('aliasValue', {
                onChange: (e) => {
                  if (selectedAliasType?.value === 'PIN') {
                    e.target.value = e.target.value.toUpperCase();
                  }

                  return e;
                },
              }),
              maxLength: aniPayTypes?.find((item) => item.value === selectedAliasType?.value)?.maxLength,
            }}
            isValid={!!errors.aliasValue?.message}
            errorText={errors.aliasValue?.message}
          />
        )}
      </Grid>
      {watchShowBenificiaryInfo && (
        <>
          <Grid item xs={12} md={6}>
            <CustomInput
              title={t('anipay.beneficiaryBankName')}
              placeholder={t('anipay.beneficiaryBankName')}
              rest={{ ...register('bank_name'), maxLength: 6 }}
              disabled={true}
              isValid={!!errors.bankCode?.message}
              errorText={errors.bankCode?.message}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomInput
              title={t('anipay.beneficiaryBankAccountNumber')}
              placeholder={t('anipay.beneficiaryBankAccountNumber')}
              disabled={true}
              rest={{ ...register('bank_account'), maxLength: 6 }}
            />
          </Grid>
          {watch('cr_tax_no') && (
            <Grid item xs={12} md={6}>
              <CustomInput
                title={t('anipay.beneficiaryTIN')}
                placeholder={t('anipay.beneficiaryTIN')}
                disabled={true}
                rest={{ ...register('cr_tax_no'), maxLength: 6 }}
              />
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <CustomInput
              title={t('anipay.beneficiaryName')}
              placeholder={t('anipay.beneficiaryName')}
              disabled={true}
              rest={{ ...register('cr_name'), maxLength: 6 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomInput
              title={t('anipay.beneficiaryAccountNumber')}
              placeholder={t('anipay.beneficiaryAccountNumber')}
              disabled={true}
              rest={{ ...register('cr_acc_no'), maxLength: 6 }}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default GeneralDetails;
